// Angular modules.
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { CalendarAvailabilitiesService } from 'app/calendar/services/calendar-availabilities.service';
import { CalendarUtilsService } from 'app/calendar/services/calendar-utils.service';
// Zeklub enums.
import { IBusySlot } from 'app/interfaces/availability.interface';
// Zenklub interfaces.
import { IDay, IWeek } from 'app/interfaces/calendar.interface';
import {
	ICalendarEvent,
	IWeekDayEvents,
	IWeekEvents,
	isIEvent,
} from 'app/interfaces/event.interface';
import { IRecurrencyAttempt } from 'app/interfaces/recurrent-attempts.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { CalendarService } from 'app/services/calendar.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { tableAnimation } from 'app/utils/animations.util';
// Third-party modules.
import { DateTime, Interval } from 'luxon';
import * as moment from 'moment';
import { forkJoin } from 'rxjs';

import { RecurrencyAttemptService } from './../../services/recurrency-attempt.service';

@Component({
	selector: 'app-weekcalendar',
	templateUrl: './weekcalendar.component.html',
	styleUrls: ['./weekcalendar.component.scss'],
	animations: [tableAnimation],
})
export class WeekcalendarComponent implements OnChanges, OnInit {
	// Inputs.
	@Input() activeDate: IDay;
	@Input() avaibilities;
	@Input() firstCall: boolean;
	@Input() forceRefreshEvents: boolean;
	// Outputs.
	@Output() showSelectedEvent: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() showDayEvents: EventEmitter<ISidebar> =
		new EventEmitter<ISidebar>();
	@Output() confirmRefreshEvents: EventEmitter<boolean> =
		new EventEmitter<boolean>();
	today: Date = new Date();
	arDays: any[] = [];
	weekData: IWeek;
	hours: IWeekEvents[] = [];
	activeFirstDay: Date;
	activeLastDay: Date;
	eventsListed = false;
	eventsList: ICalendarEvent[];
	busyslotList: IBusySlot[];
	recurrencyAttempts: IRecurrencyAttempt[];
	hourIntervalNow: string;
	lastDate: Date;
	// Total events of each day.
	dayTotalEvents: number[] = [];
	// Status.
	activeDayOfWeek: number;
	currentWeekVisible: boolean;
	avaibilitiesForWeek = [];
	isLoading: boolean;
	isMobile = false;

	constructor(
		private calendar: CalendarService,
		private dateTime: DateTimeService,
		private events: EventsService,
		private global: GlobalService,
		private availabilityService: AvailabilitiesService,
		private recurrencyAttemptService: RecurrencyAttemptService,
		public calendarAvailabilitiesService: CalendarAvailabilitiesService,
		public calendarUtils: CalendarUtilsService
	) {}

	ngOnInit() {
		if (this.firstCall) {
			this.firstCall = false;
			this.calendarUtils.showEventsOfDay(new Date(), this.showDayEvents);
		}
		this.isLoading = true;
		this.activeDayOfWeek = new Date().getDay();
		// Update events list.
		if (!this.eventsListed) {
			this.eventsListed = true;
			this.getEvents(true);
		}
		// Set sizes and positions.
		this.windowResized();
		// Subscription for new Events.
		this.events
			.eventEmitter()
			.subscribe((event) => this.setupSlots(event, 'events', true));
	}

	// On changes.
	ngOnChanges() {
		// Verify if is mobile screen.
		this.isMobile = this.global.isMobileDevice();
		if (this.activeDate.date !== this.lastDate || this.arDays.length === 0) {
			// let dt: Date = this.lastDate ? this.activeDate.date : new Date();
			const dt: Date = this.activeDate.date;
			const wData = this.calendar.createWeekCalendarGrid(dt);
			this.weekData = wData as IWeek;
			this.arDays = this.weekData.arDays;
		}
		if (!this.activeFirstDay || this.activeFirstDay !== this.arDays[0].day) {
			this.activeFirstDay = this.arDays[0].day;
			this.activeLastDay = this.arDays[6].day;
			this.eventsListed = false;
		}
		// Get events from period.
		if (!this.eventsListed || this.forceRefreshEvents) {
			// Update events list.
			this.getEvents(!this.isMobile);
			this.eventsListed = true;
			// Updated events.
			if (this.forceRefreshEvents) {
				this.forceRefreshEvents = false;
				this.confirmRefreshEvents.emit(false);
			}
		}
		// Get current interval.
		this.hourIntervalNow = this.dateTime.getHourInterval();
		this.avaibilitiesForWeek =
			this.calendarAvailabilitiesService.getAvaibilitiesForPeriod(
				this.avaibilities,
				DateTime.fromJSDate(this.weekData.firstDay).set({ hour: 0, minute: 0 }),
				DateTime.fromJSDate(this.weekData.lastDay).set({ hour: 23, minute: 59 })
			);
	}

	public windowResized(): void {
		this.isMobile = this.global.isMobileDevice();
	}

	private getHours() {
		const hours = [];
		for (let i = 0; i < 48; i++) {
			const currentHour: string = this.dateTime.getFormatedHoursAlt(
				Math.floor(i / 2),
				i % 2 !== 0 ? 30 : undefined
			);
			const weekdayEvents: IWeekDayEvents[] = [];
			for (let j = 0; j < 7; j++) {
				const luxonDate = DateTime.fromJSDate(this.weekData.firstDay)
					.plus({ days: j })
					.set({ hour: Math.floor(i / 2), minute: i % 2 !== 0 ? 30 : 0 });
				const endLuxonSlotDate = luxonDate.plus({ minutes: 30 });
				const wde: IWeekDayEvents = {
					luxonDate,
					interval: Interval.fromDateTimes(luxonDate, endLuxonSlotDate),
					weekDay: j,
					events: [],
					busySlots: [],
					recurrencyAttempts: [],
				};
				weekdayEvents.push(wde);
			}
			const h: IWeekEvents = {
				hour: currentHour,
				weekEvents: weekdayEvents,
			};
			hours.push(h);
		}

		return hours;
	}

	private setupSlots(ev: any, eventList: string, update = false) {
		let dayEnd = moment(ev.dayEnd || ev.endDate);
		const day = moment(ev.day || ev.startDate);
		const startOfDay = moment(ev.day || ev.startDate)
			.add(1, 'day')
			.startOf('day');

		// in case it surpasses the end of the day
		if (dayEnd.isAfter(startOfDay)) {
			if (isIEvent(ev)) {
				this.setupSlots(
					{ ...ev, day: startOfDay.toISOString() },
					eventList,
					update
				);
				ev.dayEnd = startOfDay.toISOString();
				dayEnd = startOfDay.clone();
			} else {
				this.setupSlots(
					{ ...ev, startDate: startOfDay.toISOString() },
					eventList,
					update
				);
				ev.endDate = startOfDay.toISOString();
				dayEnd = startOfDay.clone();
			}
		}

		const evWeekday: number = day.weekday();
		const evHour: number = day.hours();
		const evMinutes: number = day.minutes();

		// We use a cell height of 25px, so we should add a offset proportionally to it (25px/30min)
		const cellProportion = 25 / 30;
		ev.duration = dayEnd.diff(day, 'minutes');
		ev.calendarHeight = `${ev.duration * cellProportion}px`;
		ev.calendarOffset = `${
			(evMinutes >= 30 ? evMinutes - 30 : evMinutes) * cellProportion
		}px`;
		ev.calendarTime = `${ev.startTime} - ${ev.endTime}`;
		ev.isQuarterHour = ev.duration <= 15;

		const hoursIndex: number = evHour * 2 + (evMinutes >= 30 ? 1 : 0);
		this.hours[hoursIndex]?.weekEvents?.forEach((item: IWeekDayEvents) => {
			if (item.weekDay === evWeekday) {
				const idx = item[eventList].findIndex((e) => ev.id === e.id);
				if (idx !== -1 && update) {
					if (isIEvent(ev) && ev.status === 'cancelled') {
						item[eventList].splice(idx, 1);
						--this.dayTotalEvents[item.weekDay];
					} else {
						// just update the event;
						item[eventList][idx] = ev;
					}
				} else {
					item[eventList].push(ev);
					++this.dayTotalEvents[item.weekDay];
				}
			}
		});
	}

	private async setupEvents() {
		// Get first and last days of current month.
		const firstDay: Date = new Date(
			this.arDays[0].year,
			this.arDays[0].month,
			this.arDays[0].day
		);
		const lastDay: Date = new Date(
			firstDay.getFullYear(),
			firstDay.getMonth(),
			firstDay.getDate() + 6
		);

		// Current week is visible?
		this.currentWeekVisible = this.isWeekVisible(firstDay, lastDay);
		const busyslots: IBusySlot[] = await this.availabilityService.getBusySlots(
			firstDay,
			lastDay
		);
		if (busyslots) {
			this.busyslotList = busyslots;
			busyslots.forEach((bs) => this.setupSlots(bs, 'busySlots'));
		}

		const isoStartDate = DateTime.fromJSDate(this.weekData.firstDay, {
			zone: 'UTC',
		})
			.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
			.toISO();
		const isoEndDate = DateTime.fromJSDate(this.weekData.lastDay, {
			zone: 'UTC',
		})
			.set({ hour: 23, minute: 59, second: 59, millisecond: 59 })
			.toISO();

		forkJoin([
			this.events.getEvents(firstDay, lastDay),
			this.recurrencyAttemptService.getRecurrenciesAttempts({
				startDate: isoStartDate,
				endDate: isoEndDate,
			}),
		]).subscribe(([events, recurrencyAttempts]) => {
			this.recurrencyAttempts =
				this.recurrencyAttemptService.getFormattedRecurrencyAttempts(
					recurrencyAttempts?.data
				);
			if (events) {
				events.forEach(async (curEvent) => {
					if (curEvent.status !== 'cancelled') {
						this.setupSlots(curEvent, 'events');
					}
				});
				this.eventsList = events;
				this.setupRecurrencyAttempts();
			}

			this.isLoading = false;
			this.calendarUtils.scrollToHour();
		});
	}

	private getEvents(showLoader = true): void {
		this.isLoading = showLoader;
		this.dayTotalEvents = [0, 0, 0, 0, 0, 0, 0];
		this.hours = this.getHours();
		this.setupEvents();
	}

	private setupRecurrencyAttempts() {
		this.recurrencyAttempts.forEach((recurrencyAttempt) => {
			const startDate = recurrencyAttempt.interval.start;
			const endDate = recurrencyAttempt.interval.end;
			const luxonSlotHour = startDate.set({
				hour: startDate.hour,
				minute: startDate.minute < 30 ? 0 : 30,
			});
			const duration = endDate.diff(startDate, 'minutes').minutes;

			this.hours.forEach((hour, hourIndex) => {
				if (hour.hour === luxonSlotHour.toFormat('HH:mm')) {
					hour.weekEvents.forEach((weekday, weekIndex) => {
						const hasReccurrencyAttemptOverlay =
							this.recurrencyAttemptService.hasReccurrencyAttemptOverlay(
								this.recurrencyAttempts,
								weekday.interval
							);
						if (weekday.weekDay === startDate.weekday) {
							this.hours[hourIndex].weekEvents[
								weekIndex
							].recurrencyAttempts.push({
								...recurrencyAttempt,
								duration,
							});
						}
						// set if slot has recurrency overlaping
						this.hours[hourIndex].weekEvents[
							weekIndex
						].hasRecurrencyOverlaping = hasReccurrencyAttemptOverlay;
					});
				}
			});
		});
	}

	private isWeekVisible(minDate: Date, maxDate: Date): boolean {
		return this.dateTime.isInRange(new Date(), minDate, maxDate);
	}
}
